<template>
    <div
        class="d-flex flex-column overflow-hidden flex-grow-1"
        v-if="isVisible"
    >
        <b-container>
            <b-row>
                <b-col class="mt-1 mb-1">
                    <x-checkbox
                        v-model="includeInactiveLayers"
                        :text="$t('labelIncludeInactiveLayers')"
                    ></x-checkbox> </b-col
            ></b-row>
            <b-row>
                <b-col>
                    <x-select
                        :name="'slcLayers'"
                        :options="layersDropdownSelection"
                        v-model="selectedLayer"
                        :required="true"
                        :simpleMode="true"
                    />
                </b-col>
            </b-row>
        </b-container>
        <template v-if="filterData">
            <div class="scrollable px-2 mb-4 flex-grow-1">
                <b-container>
                    <template v-for="filter in filterData.filters">
                        <filter-options
                            v-if="filter.settings.visible"
                            :key="`advanced_search_${filter.settings.id}`"
                            :filterData="filter.settings"
                            :layerId="selectedLayer"
                            @update="$_updatedFilter($event)"
                        />
                    </template>
                </b-container>
            </div>
            <b-container>
                <b-row>
                    <b-col cols="12" sm="8" class="pb-4">
                        <x-checkbox
                            v-if="showOnlyViewport"
                            :id="`checkboxsearchViewport`"
                            :name="`checkboxsearchViewport`"
                            v-model="searchViewport"
                            :text="$t('onlyViewport')"
                            class="text-nowrap"
                        />
                    </b-col>
                    <b-col cols="12" sm="4" class="text-right pb-4">
                        <b-button
                            variant="primary"
                            type="button"
                            :class="{ saving: isSaving }"
                            :disabled="searchDisabled"
                            @click="$_search()"
                        >
                            <span v-if="!isSaving">{{
                                $t('searchButtonText')
                            }}</span>
                            <span
                                :class="{ 'fas fa-sync fa-spin': isSaving }"
                                v-if="isSaving"
                            ></span>
                        </b-button>
                    </b-col>
                </b-row>
            </b-container>
        </template>
        <template v-if="!filterData && selectedLayer">
            <natural-person v-if="selectedLayer === LAYER_OPTIONS.STUF" />
            <kvk-company v-if="selectedLayer === LAYER_OPTIONS.KVK" />
            <brk-properties v-if="selectedLayer === LAYER_OPTIONS.BRK" />
            <nedbrk-properties v-if="selectedLayer === LAYER_OPTIONS.NEDBRK" />
            <brp-properties v-if="selectedLayer === LAYER_OPTIONS.BRP" />
            <woz-properties v-if="selectedLayer === LAYER_OPTIONS.WOZ" />
        </template>
        <template v-else>
            <b-container>
                <b-row>
                    <b-col v-if="!selectedLayer && !loadingThemeData">
                        {{ $t('selectALayer') }}
                    </b-col>
                </b-row>
            </b-container>
        </template>
        <template v-if="loadingThemeData">
            <b-container>
                <b-row>
                    <b-col>
                        {{ $t('loading') }}
                        <b-spinner
                            variant="primary"
                            label="Spinning"
                            small
                        ></b-spinner>
                    </b-col>
                </b-row>
            </b-container>
        </template>
    </div>
</template>

<script>
    import { mapGetters, mapState, mapActions } from 'vuex';
    import {
        INTERFACE_PANEL_TRIGGERS,
        SELECTION_TYPES,
        NG_LAYER_TYPES,
        FIELD_TYPE,
        VALUE_INPUT_TYPE,
        DOMAIN_TYPE
    } from '../../../constants';
    import {
        arraySortAscendingFunction,
        findUserSavedFilterEqualToDashboardFilterFunction,
        nprogress
    } from '../../../services/utilities';
    import { isSmallDevice } from '../../../services/utilities/devices';
    import { getAttributes } from '../../../services/selections';
    import { getCombinedOlId } from '../../../services/theme';
    import FilterOptions from '../panels/filters/FilterOptions.vue';
    import NaturalPerson from './NaturalPerson.vue';
    import KvkCompany from './KvkCompany.vue';
    import BrkProperties from './BrkProperties.vue';
    import NedBrkProperties from './NedBrkProperties.vue';
    import BrpProperties from './BrpProperties.vue';
    import WozProperties from './WozProperties.vue';
    import XCheckbox from '../../BaseComponents/x-checkbox.vue';

    const LAYER_OPTIONS = {
        STUF: 'option-stuf',
        KVK: 'option-kvk',
        TABLE: 'option-table',
        BRK: 'option-brk',
        NEDBRK: 'option-nedbrk',
        BRP: 'option-brp',
        WOZ: 'option-woz'
    };

    export default {
        name: 'advanced-search',

        components: {
            'filter-options': FilterOptions,
            'natural-person': NaturalPerson,
            'kvk-company': KvkCompany,
            'brk-properties': BrkProperties,
            'nedbrk-properties': NedBrkProperties,
            'brp-properties': BrpProperties,
            'woz-properties': WozProperties,
            XCheckbox
        },

        props: { filterObject: { type: Object, required: false, default: null } },

        data() {
            return {
                selectedLayer: null,
                appliedFilters: [],
                results: null,
                isSaving: false,
                searchViewport: true,
                layerData: undefined,
                LAYER_OPTIONS: LAYER_OPTIONS,
                localFilterObject: null,
                loadingThemeData: false,
                includeInactiveLayers: false
            };
        },

        mounted() {
            this.$_startListening();
        },

        beforeDestroy() {
            this.$_stopListening();
        },

        computed: {
            ...mapState([
                'userIsBrkAuthorized',
                'userIsNedBrkAuthorized',
                'userIsBrpAuthorized',
                'userIsWozAuthorized',
                'userIsStufAuthorized',
                'userIsAllowedStufPersonQueries',
                'userIsKvkAuthorized',
                'tableSearchResults'
            ]),
            ...mapGetters([
                'getSearchableWFSLayers',
                'getActivatedFeedbacks',
                'getLayerById',
                'getMainMap',
                'isMobile',
                'getWebTheme',
                'getTableSearchResultById'
            ]),
            isFeedback() {
                return this.layerData?.type === NG_LAYER_TYPES.FEEDBACK;
            },
            isVisible: function () {
                return this.$store.state.uiElements[
                    INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH
                ].visible;
            },
            layersDropdownSelection: function () {
                let layerSelection = this.getSearchableWFSLayers
                    .filter((l) => l.active || this.includeInactiveLayers)
                    .map((layer) => ({
                        value: layer.id,
                        text: layer.title
                    }));

                if (
                    this.userIsStufAuthorized &&
                    this.userIsAllowedStufPersonQueries
                ) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.STUF,
                        text: this.$t('stuf.naturalPerson')
                    });
                }
                if (this.userIsKvkAuthorized) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.KVK,
                        text: this.$t('kvk.companies')
                    });
                }
                if (this.userIsBrkAuthorized) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.BRK,
                        text: this.$t('brk.cadastralProperties')
                    });
                }
                if (this.userIsNedBrkAuthorized) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.NEDBRK,
                        text: this.$t('nedbrk.cadastralProperties')
                    });
                }
                if (this.userIsBrpAuthorized) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.BRP,
                        text: this.$t('brp.persons')
                    });
                }
                if (this.userIsWozAuthorized) {
                    layerSelection.push({
                        value: LAYER_OPTIONS.WOZ,
                        text: this.$t('woz.objects')
                    });
                }
                // Add table results with search fields
                const resultsWithSearchFields = this.tableSearchResults.filter(
                    (result) => result.hasTableSearchFields
                );
                const tableSelection = resultsWithSearchFields.map((layer) => ({
                    value: `${LAYER_OPTIONS.TABLE}${layer.id}`,
                    text: layer.title
                }));
                layerSelection.push(...tableSelection);
                if (this.isMobile) {
                    let feedbackSelection = this.getActivatedFeedbacks.map(
                        (layer) => ({
                            value: layer.id,
                            text: layer.extended.title
                        })
                    );
                    layerSelection.push(...feedbackSelection);
                }
                return layerSelection.sort(arraySortAscendingFunction('text'));
            },
            filterDataOriginal() {
                if (!this.layerData) {
                    return undefined;
                }
                return {
                    customId: this.layerData.id,
                    filters:
                        this.layerData?.filters?.data || this.layerData?.filters
                };
            },
            filterData() {
                if (this.isFeedback) {
                    return this.feedbackData;
                }
                if (!this.filterDataOriginal) {
                    return undefined;
                }
                const filters = this.filterDataOriginal.filters.map(
                    (dashboardFilter) => {
                        if (this.localFilterObject?.filters) {
                            // if this filter is used in filterObject use this filter otherwise use original filter from dashboard
                            const savedFilters = {
                                filters: this.localFilterObject.filters.map(
                                    (filter) => ({ filter })
                                )
                            };
                            const foundSearchedFilter =
                                findUserSavedFilterEqualToDashboardFilterFunction(
                                    savedFilters.filters
                                )(dashboardFilter.settings)?.filter;
                            if (foundSearchedFilter) {
                                return { settings: foundSearchedFilter };
                            }
                        }
                        return dashboardFilter;
                    }
                );
                return { ...this.filterDataOriginal, filters };
            },
            feedbackData() {
                const reservedFeedbackFieldNames = [
                    'verwijderddoor',
                    'verwijderdatum'
                ];

                const reservedFeedbackFieldTypes = [
                    FIELD_TYPE.FOTOFIELD_TYPE,
                    FIELD_TYPE.VIDEO
                ];

                if (!this.layerData?.extended?.feedbackFields) {
                    return null;
                }

                const feedbackFields = getAttributes(this.layerData);
                const filters = feedbackFields
                    .filter((ff) => {
                        return reservedFeedbackFieldNames.indexOf(ff.name) < 0;
                    })
                    .filter((ff) => {
                        return reservedFeedbackFieldTypes.indexOf(ff.type) < 0;
                    })
                    .filter((ff) => {
                        // hide personal data fields in web, show in mobile
                        return !ff.personalData || this.isMobile;
                    })
                    .map((ff) => ({
                        ...ff,
                        type: this.$_getFeedbackType(ff),
                        domain: this.$_getDomain(ff),
                        domainType: this.$_getDomainType(ff)
                    }))
                    .map((ff) => ({
                        settings: {
                            caseSensitive: false,
                            // if field has a datatype use that. Otherwise determine datatype from type
                            dataType:
                                ff.dataType ?? this.$_getFeedbackDataType(ff.type),
                            domainId: ff.domain?.id,
                            domainType: ff.domain && ff.domainType,
                            feedbackDomain: ff.domain,
                            filterAttribute: ff.name,
                            id: ff.id,
                            operator: '=',
                            operatorEditable: true,
                            name: ff.alias,
                            selected: false,
                            value: '',
                            visible:
                                ff.displayInDetail || ff.displayInTable || ff.popup,
                            valueInput:
                                ff.type === FIELD_TYPE.DATE
                                    ? VALUE_INPUT_TYPE.DATEFIELD
                                    : null
                        }
                    }));
                return {
                    customId: this.layerData.id,
                    filters
                };
            },
            searchDisabled() {
                // Search disabled when no visible items selected
                let disabled = true;
                if (
                    this.appliedFilters.length > 0 &&
                    this.appliedFilters.some((f) => f.visible)
                ) {
                    disabled = false;
                }
                return disabled;
            },
            isTableSearchResult() {
                return (
                    this.selectedLayer &&
                    typeof this.selectedLayer === 'string' &&
                    this.selectedLayer.startsWith(LAYER_OPTIONS.TABLE)
                );
            },
            // Determine if the "restrict search to viewing area" checkbox must be shown
            showOnlyViewport() {
                // Do not show if we are searching a table
                if (this.isTableSearchResult) {
                    return false;
                }

                // A webtheme contains a setting to set this option on or off
                if (this.$store.getters.isWeb) {
                    return this.getWebTheme.data.showOnlyViewport;
                }

                return true;
            },
            // The default value for this.searchViewport is either the webTheme setting or true.
            searchViewportDefaultValue() {
                if (this.$store.getters.isWeb) {
                    return this.getWebTheme?.data?.onlyViewportDefaultValue;
                } else {
                    return true;
                }
            }
        },

        watch: {
            isVisible(newVal) {
                if (newVal) {
                    this.appliedFilters = [];
                }
            },
            layersDropdownSelection(newVal) {
                // If the list of searchable layers has changed check if current selection is still in it.
                let layer = newVal.findIndex((n) => n.value === this.selectedLayer);

                // If not clear current selection
                if (layer === -1) {
                    this.selectedLayer = null;
                }
            },
            async selectedLayer(newVal) {
                this.appliedFilters = [];
                if (newVal) {
                    if (this.isTableSearchResult) {
                        const id = parseInt(
                            newVal.replace(LAYER_OPTIONS.TABLE, '')
                        );
                        await this.getTableSearchFields(id);
                        this.layerData = this.getTableSearchResultById(id);
                    } else {
                        this.layerData = this.getLayerById(newVal);
                        // If the layer is not a API layer (id starts with "option" and
                        // If the themelayer is not yet loaded, load it now
                        if (!newVal.startsWith('option') && !this.layerData) {
                            nprogress.start();
                            this.loadingThemeData = true;
                            let themeId = parseInt(newVal.split('-')[0]);

                            await this.$store.dispatch('loadTheme', {
                                themeId: themeId,
                                toggledTheme: false
                            });
                            this.layerData = this.getLayerById(newVal);
                            this.loadingThemeData = false;
                            nprogress.done();
                        }
                    }
                } else {
                    this.layerData = undefined;
                }
            },
            filterData(newVal) {
                this.appliedFilters = [];
                if (newVal?.filters) {
                    newVal.filters.forEach(({ settings }) => {
                        this.$_updatedFilter(settings);
                    });
                    // When a filterObject is provided from outside the component and it contains filters
                    // this will automatically execute the search.
                    // Set filters to null to execute this only once.
                    if (this.localFilterObject?.filters) {
                        this.localFilterObject.filters = null;
                        this.$_searchItems(this.localFilterObject.bbox, false);
                    }
                }
            },
            filterObject: {
                handler: function (newVal) {
                    if (!newVal?.themeId) {
                        return;
                    }
                    this.localFilterObject = JSON.parse(JSON.stringify(newVal));
                    this.selectedLayer = getCombinedOlId(
                        newVal.themeId,
                        newVal.layerId
                    );
                    this.searchViewport = newVal.searchViewport;
                },
                immediate: true,
                deep: true
            },
            localFilterObject: function (newValue) {
                if (
                    JSON.stringify(newValue) === JSON.stringify(this.filterObject)
                ) {
                    return;
                }
                const locFilterObject = JSON.parse(
                    JSON.stringify(this.localFilterObject)
                );
                this.$emit('update', locFilterObject);
            },
            // Assign the default value for this.searchViewport in a watch to allow for late loading of
            // the webtheme data.
            searchViewportDefaultValue: function (newValue) {
                this.searchViewport = newValue;
            }
        },

        methods: {
            ...mapActions(['searchTableData', 'getTableSearchFields']),
            $_startListening() {
                document.addEventListener('keyup', this.$_onKeyUp);
            },
            $_stopListening() {
                document.removeEventListener('keyup', this.$_onKeyUp);
            },
            $_onKeyUp: function (event) {
                if (event.keyCode === 13 && !this.searchDisabled) {
                    this.$_search();
                }
            },
            $_getDomainType(feedbackField) {
                return feedbackField.type === FIELD_TYPE.CATEGORY
                    ? DOMAIN_TYPE.DROPDOWNLIST
                    : feedbackField.domainType;
            },
            $_getDomain(feedbackField) {
                if (feedbackField.type === FIELD_TYPE.CATEGORY) {
                    return {
                        id: 'dummyId',
                        keyvalues: feedbackField.feedbackFieldCategoryLookups.map(
                            (cat, index) => {
                                return {
                                    id: index,
                                    key: cat.feedbackCategory.name,
                                    value: cat.feedbackCategory.name
                                };
                            }
                        )
                    };
                }
                return feedbackField.domain;
            },
            $_getFeedbackType(feedbackField) {
                const dateFields = [
                    'aanmaakdatum',
                    'verwijderdatum',
                    'wijzigingdatum'
                ];
                const index = dateFields.findIndex(
                    (item) => item === feedbackField.name
                );
                return index !== -1 ? FIELD_TYPE.DATE : feedbackField.type;
            },
            $_getFeedbackDataType(type) {
                switch (type) {
                    case FIELD_TYPE.NUMERIC:
                    case FIELD_TYPE.NUMERICWITHDECIMALS:
                        return 'xsd:int';
                    case FIELD_TYPE.DATE:
                        return 'xsd:date';
                    default:
                        return 'xsd:string';
                }
            },
            $_updatedFilter(filter) {
                this.appliedFilters = [
                    ...this.appliedFilters.filter(({ id }) => id !== filter.id),
                    filter
                ].filter(({ selected }) => selected);
            },
            async $_search() {
                const mapObject = this.getMainMap;
                const map = mapObject.map;
                const bbox = this.searchViewport
                    ? map.getView().calculateExtent(map.getSize())
                    : this.$store.getters.getOrganizationBBox;
                this.$_searchItems(bbox, !this.isFeedback);
            },
            async $_searchItems(bbox, saveUserSearch) {
                this.isSaving = true;
                this.results = [];
                this.$emit('pinPanel', true);
                this.$store.commit('CLEAR_SELECTIONS');
                this.$store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');
                this.$store.commit('TOGGLE_ADVANCED_SEARCH_PANEL', true);
                if (this.appliedFilters.length > 0) {
                    if (this.isTableSearchResult) {
                        await this.$_searchTableData();
                    } else {
                        await this.$_searchFeatures(bbox, saveUserSearch);
                    }
                }
                this.$store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
                this.isSaving = false;
            },
            $_searchTableData() {
                try {
                    const selectedLayerOption = this.layersDropdownSelection.find(
                        (option) => option.value === this.selectedLayer
                    );
                    this.searchTableData({
                        title: selectedLayerOption.text,
                        id: parseInt(
                            this.selectedLayer.replace(LAYER_OPTIONS.TABLE, '')
                        ),
                        objectData: this.appliedFilters
                    });
                    this.$emit('pinPanel', false);
                } catch {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: `${this.$t('errorWhenSearching')}`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                }
            },
            async $_searchFeatures(bbox, saveUserSearch) {
                try {
                    const features = await this.$store.dispatch(
                        'searchFeaturesOnLayerWithCustomFilters',
                        {
                            bbox,
                            layerId: this.selectedLayer,
                            filters: this.appliedFilters
                        }
                    );
                    this.results = features;
                    this.$_showResults(features);
                    // Show value in searchfield
                    if (!this.$store.getters.isWeb) {
                        if (saveUserSearch) {
                            this.$store.commit('UPDATE_NAVBAR_USER_SEARCH', {
                                themeId: this.layerData.data.themeId,
                                themeLayerId: this.layerData.data.id,
                                content: {
                                    layerTitle: this.layerData.data.title,
                                    lastDate: new Date().getTime(),
                                    searchViewport: this.searchViewport,
                                    featureCount: this.results.length,
                                    bbox,
                                    filters: this.appliedFilters
                                }
                            });
                        } else {
                            // Clear previous value in searchfield
                            this.$store.commit('UPDATE_NAVBAR_USER_SEARCH', null);
                        }
                    }
                } catch {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: `${this.$t('errorWhenSearching')}`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                }
            },
            $_showResults(features) {
                if (!features || features.length === 0) {
                    const message = this.searchViewport
                        ? 'noResultsFoundInArea'
                        : 'noResultsFound';
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: this.$t(message),
                        variant: 'warning',
                        autoHideDelay: 5000
                    });
                } else {
                    let meta;
                    if (this.layerData.type === 'theme') {
                        const theme = this.$store.getters.getThemeById(
                            this.layerData.data.themeId
                        );
                        meta = {
                            type: this.layerData.type,
                            theme
                        };
                    }

                    if (this.layerData.type === 'feedback') {
                        const feedback = this.$store.getters.getFeedbackById(
                            this.layerData.data.feedbackId
                        );
                        meta = {
                            type: this.layerData.type,
                            feedback
                        };
                    }

                    const results = [
                        {
                            layer: this.layerData,
                            features,
                            meta,
                            limitedResults: false
                        }
                    ];

                    this.$emit('pinPanel', false);
                    this.$store.dispatch('displaySelection', {
                        data: {
                            location: null,
                            results,
                            type: SELECTION_TYPES.SEARCH
                        }
                    });

                    // Do not zoom to features in web when it has no tableView, to avoid zooming in twice
                    const isWebWithTableView =
                        this.getWebTheme?.data.showTableView &&
                        (!isSmallDevice() ||
                            this.getWebTheme?.data.showTableViewSmallDevices);
                    if (this.isMobile || isWebWithTableView) {
                        this.$store.dispatch('zoomToFeatures', {
                            mapName: 'main',
                            features,
                            zoomWhenOutOfView: true,
                            minResolution: this.getMainMap?.viewport?.resolution
                        });
                    }
                }
            }
        }
    };
</script>

<style scoped>
.scrollable {
    overflow: hidden;
    overflow-y: auto;
}
</style>
